import React, { useState } from 'react';
import { X, ChevronDown } from 'lucide-react';
import './AddPatientModal.css';

const AddPatientModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    emailAddress: '',
    cellPhone: '',
    site: ''
  });

  const [errors, setErrors] = useState({});

  const sites = [
    'Site A - Main Hospital',
    'Site B - North Clinic', 
    'Site C - South Clinic',
    'Site D - East Campus',
    'Site E - West Campus'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    if (!formData.cellPhone.trim()) {
      newErrors.cellPhone = 'Cell phone is required';
    } else if (!/^\(\d{3}\) \d{3}-\d{4}$/.test(formData.cellPhone) && !/^\d{10}$/.test(formData.cellPhone.replace(/\D/g, ''))) {
      newErrors.cellPhone = 'Please enter a valid phone number';
    }

    if (!formData.site) {
      newErrors.site = 'Site selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    handleInputChange('cellPhone', formatted);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Generate a new patient ID (in real app, this would come from backend)
      const newPatientId = Math.floor(Math.random() * 900) + 100;
      
      const newPatient = {
        id: newPatientId,
        emailAddress: formData.emailAddress,
        cellPhone: formData.cellPhone,
        site: formData.site,
        // Default values for new patients
        avatar: null,
        weekTrend: '0%',
        scheduleAdherence: '0%',
        overallAdherence: '0%',
        dosingOpportunities: '0 / 0 (0)',
        daysInTreatment: 0,
        doseAdminTime: 0,
        away: false,
        pillboxStatus: 'Not Assigned',
        nextFollowUp: 'TBD'
      };

      onSubmit(newPatient);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      emailAddress: '',
      cellPhone: '',
      site: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="add-patient-modal-overlay" onClick={handleClose}>
      <div className="add-patient-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="add-patient-modal-header">
          <div className="modal-title-section">
            <h2>Add Patient</h2>
            <p className="modal-subtitle">Invite a new patient to join the study</p>
          </div>
          <button className="modal-close-btn" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        <form className="add-patient-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="emailAddress" className="form-label">Email Address</label>
            <input
              id="emailAddress"
              type="email"
              value={formData.emailAddress}
              onChange={(e) => handleInputChange('emailAddress', e.target.value)}
              className={`form-input ${errors.emailAddress ? 'error' : ''}`}
              placeholder="Enter patient's email address"
            />
            {errors.emailAddress && (
              <span className="error-message">{errors.emailAddress}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cellPhone" className="form-label">Cell Phone</label>
            <input
              id="cellPhone"
              type="tel"
              value={formData.cellPhone}
              onChange={handlePhoneChange}
              className={`form-input ${errors.cellPhone ? 'error' : ''}`}
              placeholder="(555) 123-4567"
              maxLength={14}
            />
            {errors.cellPhone && (
              <span className="error-message">{errors.cellPhone}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="site" className="form-label">Site</label>
            <div className="select-wrapper">
              <select
                id="site"
                value={formData.site}
                onChange={(e) => handleInputChange('site', e.target.value)}
                className={`form-select ${errors.site ? 'error' : ''}`}
              >
                <option value="">Select a site</option>
                {sites.map((site) => (
                  <option key={site} value={site}>
                    {site}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="select-icon" />
            </div>
            {errors.site && (
              <span className="error-message">{errors.site}</span>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPatientModal;

